import { almostEqual } from "overline";
import type { MergeStore } from "./merge-store.js";

const EPSILON = 1e-8;

interface Point {
  x: number;
  y: number;
}

interface LinearTotals {
  total_perimeter: number;
  vertical_perimeter_length: number;
  horizontal_perimeter_length: number;
  raked_perimeter_length: number;
  vertical_gridline_length: number;
  horizontal_gridline_length: number;
  total_gridline_length: number;
}

function computeLinearTotals(
  polygon: Point[],
  horizontalGridlines: number[],
  verticalGridlines: number[],
  mergeStore?: MergeStore
): LinearTotals {
  // --- Perimeter ---
  let total_perimeter = 0;
  let vertical_perimeter_length = 0;
  let horizontal_perimeter_length = 0;
  let raked_perimeter_length = 0;

  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const length = Math.hypot(dx, dy);
    total_perimeter += length;

    if (almostEqual(a.x, b.x)) {
      vertical_perimeter_length += length;
    } else if (almostEqual(a.y, b.y)) {
      horizontal_perimeter_length += length;
    } else {
      raked_perimeter_length += length;
    }
  }

  // --- Gridlines ---
  const numRows = horizontalGridlines.length - 1;
  const numCols = verticalGridlines.length - 1;

  let vertical_gridline_length = 0;
  let horizontal_gridline_length = 0;

  for (let j = 0; j <= numCols; j++) {
    const x = verticalGridlines[j];
    for (let r = 0; r < numRows; r++) {
      const y0 = horizontalGridlines[r];
      const y1 = horizontalGridlines[r + 1];
      if (mergeStore && !segmentSurvivesVertical(mergeStore, r, j, numCols)) continue;
      vertical_gridline_length += clipVerticalSegmentToPolygon(
        x, y0, y1, polygon
      );
    }
  }

  for (let i = 0; i <= numRows; i++) {
    const y = horizontalGridlines[i];
    for (let c = 0; c < numCols; c++) {
      const x0 = verticalGridlines[c];
      const x1 = verticalGridlines[c + 1];
      if (mergeStore && !segmentSurvivesHorizontal(mergeStore, i, c, numRows)) continue;
      horizontal_gridline_length += clipHorizontalSegmentToPolygon(
        y, x0, x1, polygon
      );
    }
  }

  return {
    total_perimeter,
    vertical_perimeter_length,
    horizontal_perimeter_length,
    raked_perimeter_length,
    vertical_gridline_length,
    horizontal_gridline_length,
    total_gridline_length: vertical_gridline_length + horizontal_gridline_length,
  };
}

function segmentSurvivesVertical(
  mergeStore: MergeStore,
  r: number,
  j: number,
  numCols: number
): boolean {
  if (j === 0 || j === numCols) return true;
  const left = mergeStore.getMergeAt(r, j - 1);
  const right = mergeStore.getMergeAt(r, j);
  if (left === null || right === null) return true;
  return left !== right;
}

function segmentSurvivesHorizontal(
  mergeStore: MergeStore,
  i: number,
  c: number,
  numRows: number
): boolean {
  if (i === 0 || i === numRows) return true;
  const above = mergeStore.getMergeAt(i - 1, c);
  const below = mergeStore.getMergeAt(i, c);
  if (above === null || below === null) return true;
  return above !== below;
}

/**
 * Total length of a vertical line segment (x, y0) -> (x, y1) that lies
 * strictly inside the polygon. Polygon edges that are nearly vertical
 * AND nearly aligned with x=const are skipped, since they coincide with
 * the gridline and shouldn't contribute to the gridline length.
 */
function clipVerticalSegmentToPolygon(
  x: number,
  y0: number,
  y1: number,
  polygon: Point[]
): number {
  // If any polygon edge coincides with this gridline, the gridline is on
  // the boundary, not in the interior — contribute nothing.
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    if (Math.abs(a.x - x) < EPSILON && Math.abs(b.x - x) < EPSILON) {
      return 0;
    }
  }

  const intersections: number[] = [];
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    if ((a.x <= x && b.x > x) || (b.x <= x && a.x > x)) {
      const t = (x - a.x) / (b.x - a.x);
      intersections.push(a.y + t * (b.y - a.y));
    }
  }
  intersections.sort((p, q) => p - q);

  let total = 0;
  for (let k = 0; k + 1 < intersections.length; k += 2) {
    const lo = Math.max(intersections[k], y0);
    const hi = Math.min(intersections[k + 1], y1);
    if (hi > lo) total += hi - lo;
  }
  return total;
}

function clipHorizontalSegmentToPolygon(
  y: number,
  x0: number,
  x1: number,
  polygon: Point[]
): number {
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    if (Math.abs(a.y - y) < EPSILON && Math.abs(b.y - y) < EPSILON) {
      return 0;
    }
  }

  const intersections: number[] = [];
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    if ((a.y <= y && b.y > y) || (b.y <= y && a.y > y)) {
      const t = (y - a.y) / (b.y - a.y);
      intersections.push(a.x + t * (b.x - a.x));
    }
  }
  intersections.sort((p, q) => p - q);

  let total = 0;
  for (let k = 0; k + 1 < intersections.length; k += 2) {
    const lo = Math.max(intersections[k], x0);
    const hi = Math.min(intersections[k + 1], x1);
    if (hi > lo) total += hi - lo;
  }
  return total;
}

export { computeLinearTotals }
